import { pointerWithin, rectIntersection } from "@dnd-kit/core";
import { strictRowStringToCode } from "@package/grid-utils";
import type { Column } from "ag-grid-community";
import dayjs, { type Dayjs } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import type { User_Right_Enum } from "../__generated__/gql/graphql";
import type { TGlobalProduct, TProduct } from "../data";
import { splitCellRef } from "../data/formatting/cell-ref";
import type { TColumnHeaderProps } from "./market-grid/components";

dayjs.extend(utc);
dayjs.extend(timezone);

export const utcNow = () => dayjs().utc();

export function colParams(column: Column | null | undefined): TColumnHeaderProps | undefined {
  if (!column) return undefined;
  return column?.getUserProvidedColDef()?.headerComponentParams;
}

/**
 * Field selector to UOM label mapping.
 * For non-price fields (like OI, volume, bid/ask sizes), we show the selector-specific label
 * instead of the product's price UOM.
 */
const fieldSelectorUom: Record<string, string> = {
  ask: "Ask",
  asksize: "Ask lots",
  bid: "Bid",
  bidsize: "Bid lots",
  chgexch: "ChgEx",
  "chg%exch": "ChgEx%",
  chgfv: "ChgFV",
  "chgfv-mid": "ChgMid",
  chgtick: "ChgTk",
  close: "Cls",
  "days-before": "DaysBfr",
  "days-end": "DateEnd",
  "days-start": "DateStart",
  "days-total": "DaysTotal",
  expiry: "Expiry",
  "first-effective": "1st days",
  "first-used": "FirstUsed",
  high: "High",
  last: "Last",
  lastsize: "Last lots",
  lasttime: "LastTime",
  low: "Low",
  "last-used": "LastUsed",
  "pricing-days": "Px days",
  "second-effective": "2nd days",
  "settle-chg": "Settle Chg",
  "settle-prior": "Settle -1",
  "settle-today": "Settle",
  "total-pricing-days": "Tot px days",
  oi: "OI",
  oidate: "OIDate",
  oichg: "OIChg",
  volm: "Volm",
  volmprior: "Vol -1",
  "value-before": "ValBfr",
  "value-after": "ValAft",
  wav: "WAv",
  "mm-nyc": "MM-NYC",
  "mm-sin": "MM-SIN",
  "mm-lon": "MM-LON",
  "settle-date": "Settle",
};

/** Selectors that represent price values and should use the product's UOM */
const priceSelectors = new Set(["value", "fv", "fv-mid"]);
export function prettyUom(uom: string) {
  switch (uom) {
    case "usd_bbl":
      return "$/bbl";
    case "usc_bbl":
      return "c/bbl";
    case "usd_ton":
      return "$/ton";
    case "usd_mt":
      return "$/mt";
    case "usd_gal":
      return "$/gal";
    case "usc_gal":
      return "c/gal";
    case "percent":
      return "%";
    case "usd_thousands":
      return "$'000s";
    case "kg_m3":
      return "kg/m³";
    case "days":
      return "days";
    case "usd_day":
      return "$/day";
    case "usd_mmbtu":
      return "$/mmbtu";
    case "usdmm":
      return "$mm";
    case "p_thm":
      return "p/thm";
    case "eur_mwh":
      return "€/MWh";
    case "rmb_ton":
      return "CN¥/ton";
    case "rmb_bbl":
      return "CN¥/bbl";
    case "rmb_usd":
      return "CN¥/$";
    case "usd_eur":
      return "$/€";
    case "usd_gbp":
      return "$/£";
    case "eur_gbp":
      return "€/£";
    case "none":
      return "Misc";
    case "ws_and_usd_ton":
      return "ws & $/ton";
    case "factor":
      return "Factor";
    case "brl_mt":
      return "BRL/MT";
    default:
      return uom;
  }
}

export function uomToLabel({
  cellLabel,
  uomDescription,
  selector,
}: {
  cellLabel?: string | null;
  uomDescription?: string | null;
  selector?: string | null;
}) {
  // If we have a non-price selector, use the selector-specific label
  if (selector && !priceSelectors.has(selector) && fieldSelectorUom[selector]) {
    return fieldSelectorUom[selector];
  }

  // Otherwise use the product's UOM from the database
  const pretty = uomDescription?.trim();
  if (pretty) return pretty;

  return cellLabel ?? "";
}

export function fieldNameSelectorToLabel(fieldName: string) {
  return fieldName.replace("-", " ").toUpperCase();
}

/**
 * TODO: why does this subtract an hour from the London timezone?
 * this is only used in the settings panel, no where else.
 * Why, and does this make sense?
 */
export function londonDate(current: Dayjs) {
  const londonDateTime = current.tz("Europe/London");
  const date = londonDateTime.subtract(1, "hour");
  return date;
}

/**
 * Not a beautiful name - an attempt to recover a Date from `londonDate`
 */
export const londonDateAsDate = (current: Dayjs): Date => new Date(londonDate(current).valueOf());

type PackageWithSource = {
  packageByPackage?: {
    name?: string | null;
    sourceBySource?: {
      name?: string | null;
      exchange?: { code?: number | null } | null;
    } | null;
  } | null;
  globalPackageByGlobalPackage?: {
    name?: string | null;
    source?: string | number | null;
    sourceBySource?: {
      name?: string | null;
    } | null;
  } | null;
};

function packageSourceName({ packageByPackage, globalPackageByGlobalPackage }: Partial<PackageWithSource>) {
  const exchangeCode = packageByPackage?.sourceBySource?.exchange?.code;
  const formattedExchangeCode = exchangeCode === 0 || exchangeCode ? String(exchangeCode) : undefined;
  return (
    globalPackageByGlobalPackage?.sourceBySource?.name ??
    packageByPackage?.sourceBySource?.name ??
    formattedExchangeCode
  );
}

export function packageNameWithSource({ packageByPackage, globalPackageByGlobalPackage }: Partial<PackageWithSource>) {
  const packageName = globalPackageByGlobalPackage?.name ?? packageByPackage?.name;
  if (!packageName) return "";

  const sourceName = packageSourceName({ packageByPackage, globalPackageByGlobalPackage });
  return sourceName ? `${packageName} [${sourceName}]` : packageName;
}

/** Closed-input label for a selected Snapshot/EOD curve: `DISPLAY NAME: [SOURCE]`. */
export function productDisplayNameWithSource({
  name,
  packageByPackage,
  globalPackageByGlobalPackage,
}: Pick<TProduct, "name"> & Partial<PackageWithSource>) {
  const sourceName = packageSourceName({ packageByPackage, globalPackageByGlobalPackage });
  return sourceName ? `${name}: [${sourceName}]` : name;
}

export function productName({
  name,
  description,
  packageByPackage,
  globalPackageByGlobalPackage,
}: Pick<TProduct, "name" | "description"> & Partial<PackageWithSource>) {
  const hasDescription = description && description !== name;
  const displayName = hasDescription ? `${name} (${description})` : name;
  const packageLabel = globalPackageByGlobalPackage?.name ?? packageByPackage?.name;
  return packageLabel ? `${displayName}: ${packageLabel}` : displayName;
}

export function globalProductName({ name, description }: TGlobalProduct) {
  if (description && name) {
    if (description === name) {
      return name;
    }

    return `${name} (${description})`;
  }

  const displayName = description || name;
  return displayName;
}

export function productNameComparator(a: string, b: string) {
  const [a1, a2] = a.split(":");
  const [b1, b2] = b.split(":");
  const majorCompare = a1.localeCompare(b1);
  const minorCompare = a2.localeCompare(b2);
  return majorCompare === 0 ? minorCompare : majorCompare;
}

export function sortByStringCaseInsensitive<T>(k: keyof T, coll: T[]) {
  return coll.sort((a, b) => {
    const aLower = (a[k] || "").toString().toLowerCase();
    const bLower = (b[k] || "").toString().toLowerCase();
    return aLower.localeCompare(bLower);
  });
}

export function compareStrings(a: string, b: string) {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

// * TODO: is this function used anywhere? Can we delete it?
// * Extracts dependencies from a set of product IDs
// *
// * @param products - a dictionary of products with product_configs containing formulas
// * @param lookupIds - The set of product IDs to extract dependencies from
// * @returns The set of unique dependencies
// *
export function extractFormulaDependencies(
  products: {
    [key: string]: { product_configs: { formula: string | null }[] };
  },
  lookupIds: Set<string>,
): Set<string> {
  const formulaPattern = /\[(.*?)\]/g;

  // The function to extract unique dependencies from a given set of product IDs
  function deps(lookupIds: Set<string>): Set<string> {
    const result = new Set<string>();

    for (const id of lookupIds) {
      const product = products[id];

      // Safely skip if the product does not exist or has no configurations
      if (!product || !product.product_configs) continue;

      // Process each configuration
      for (const config of product.product_configs) {
        const { formula } = config;

        if (!formula) continue;

        let match: RegExpExecArray | null;
        while (true) {
          match = formulaPattern.exec(formula);
          if (match === null) break;
          result.add(match[1]);
        }
      }
    }

    return result;
  }

  const dependencies = new Set([...lookupIds].filter((id) => Object.hasOwn(products, id)));
  let toExplore = new Set([...lookupIds]);

  while (toExplore.size > 0) {
    const nextToExplore = deps(toExplore);
    toExplore = new Set<string>();

    for (const id of nextToExplore) {
      if (!dependencies.has(id)) {
        toExplore.add(id);
        dependencies.add(id);
      }
    }
  }

  return dependencies;
}

export function insert<T>(coll: T[], index: number, element: T): T[] {
  return [...coll.slice(0, index), element, ...coll.slice(index)];
}

export type Permissions = TProduct["packageByPackage"]["permissions"];
// This is essentially equivalent to read only
export function isListOnlyPermissions(permissions: Permissions | undefined | null) {
  return permissions?.length === 1 && permissions[0].permission === "list";
}

export function isReadOnlyPermissions(permissions: Permissions | undefined | null) {
  return permissions?.length === 1 && permissions[0].permission === "read";
}

export const hasPermissions = (permissions: Permissions | undefined): boolean => Boolean(permissions?.length);

export function isGivenPermission(permissions: Permissions | undefined | null, permission: User_Right_Enum) {
  return permissions?.find((entry) => entry.permission === permission);
}

export function collisionDetection(args: Parameters<typeof pointerWithin>[0]) {
  // First, let's see if there are any collisions with the pointer
  const pointerCollisions = pointerWithin(args);

  // Collision detection algorithms return an array of collisions
  if (pointerCollisions.length > 0) {
    return pointerCollisions;
  }

  // If there are no collisions with the pointer, return rectangle intersections
  return rectIntersection(args);
}

export function parseLimitRef(limitRef: string) {
  try {
    const parsed = splitCellRef(limitRef);
    if (!parsed?.period) {
      return { columnId: null, rowId: null };
    }

    const { columnId, productId, fieldSelector, period } = parsed;
    // Strip/spread formulas are the grid row id; months still need a month code.
    const rowCode = period.includes(":") || period.includes("/") ? period : strictRowStringToCode(period);
    if (!rowCode) {
      return { columnId: null, rowId: null };
    }

    return {
      columnId,
      rowId: rowCode,
      productId,
      fieldSelector,
    } as const;
  } catch (e) {
    console.error("Failed to parse limit ref", limitRef, e);
    return { columnId: null, rowId: null };
  }
}

export const tryParse = <T>(value: string | null | undefined, def: T): T => {
  if (!value) return def;
  try {
    return JSON.parse(value) as T;
  } catch {
    return def;
  }
};

const validHexCharRegex = /[0-9a-fA-F]/;
export function validHexChars(c: string[], alpha = false) {
  const length = alpha ? 8 : 6;
  const chars = c?.length > 0 && c[0] === "#" ? c.slice(1) : c;
  return chars.every((char) => validHexCharRegex.test(char)) && chars.length <= length;
}

export const compareIdx = <T extends { idx?: string }>(a: T, b: T) => {
  const aIdx = a.idx || "0";
  const bIdx = b.idx || "0";
  if (aIdx === bIdx) return 0;
  return aIdx < bIdx ? -1 : 1;
};

const variableNameFromVarRegex = /\(([^)]+)\)/;
export function getColorFromVarName(varName: string) {
  if (varName === "inherit") return "inherit";

  // If the variable is in the format var(--variable-name), get the content between the parentheses.
  const variable = varName.includes("(") ? varName?.match(variableNameFromVarRegex)?.[1] : varName;

  return getComputedStyle(document.documentElement).getPropertyValue(variable || "");
}

export const tryGetColourFromVarName = (varName: string) => {
  if (varName.startsWith("var(")) {
    const colour = getColorFromVarName(varName);
    return colour === "" ? "inherit" : colour;
  }
  return varName;
};
